import React from "react";

/**
 * Mounts global CSS that the EAS pages rely on:
 *   - Google Fonts (Fraunces / Inter / JetBrains Mono)
 *   - a few utility classes (.eas-reveal, .eas-container, .eas-btn, etc.)
 *
 * Mount once at the top of EasLandingPage / EasSystemPage. Safe to mount twice
 * — the <style> tag is keyed by id so the second mount is a no-op.
 */
export default function EasGlobalStyles(): JSX.Element {
    return (
        <>
            <link rel="preconnect" href="https://fonts.googleapis.com"/>
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous"/>
            <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,ital,wght@9..144,0,300;9..144,0,400;9..144,0,500;9..144,0,600;9..144,1,300;9..144,1,400&family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap"
            />
            <style id="eas-global-css">{EAS_CSS}</style>
        </>
    );
}

const EAS_CSS = `
.eas-root { background: #0b1410; color: rgba(255,255,255,0.9); font-family: "Inter", system-ui, sans-serif; font-size: 16px; line-height: 1.6; overflow-x: hidden; }
.eas-root *, .eas-root *::before, .eas-root *::after { box-sizing: border-box; }
.eas-root ::selection { background: #2ad167; color: #0b1410; }
.eas-container { max-width: 1240px; margin: 0 auto; padding: 0 32px; }
.eas-container--narrow { max-width: 840px; }
.eas-section { padding: 120px 0; }
.eas-section--tight { padding: 100px 0; }
.eas-section-eyebrow { font-family: "JetBrains Mono", monospace; font-size: 12px; letter-spacing: 0.18em; text-transform: uppercase; color: #5ce08d; margin: 0 0 20px; }
.eas-h2 { font-family: "Fraunces", serif; font-weight: 300; font-size: clamp(36px, 5vw, 64px); line-height: 1.02; letter-spacing: -0.025em; margin: 0 0 32px; color: #fff; text-wrap: balance; max-width: 22ch; }
.eas-h2 em { font-style: italic; color: #5ce08d; font-weight: 400; }
.eas-btn { display: inline-flex; align-items: center; gap: 10px; padding: 15px 26px; border-radius: 999px; font-weight: 600; text-decoration: none; font-size: 15px; transition: transform .2s, background .2s, box-shadow .2s; cursor: pointer; border: 0; font-family: inherit; }
.eas-btn--primary { background: #2ad167; color: #0b1410; }
.eas-btn--primary:hover { transform: translateY(-2px); box-shadow: 0 14px 40px -10px rgba(42,209,103,0.5); background: #5ce08d; }
.eas-btn--ghost { background: rgba(255,255,255,0.06); color: #fff; border: 1px solid rgba(255,255,255,0.14); }
.eas-btn--ghost:hover { background: rgba(255,255,255,0.1); }
.eas-btn--large { font-size: 17px; padding: 20px 36px; }
.eas-btn .eas-arr { transition: transform .2s; }
.eas-btn:hover .eas-arr { transform: translateX(3px); }
.eas-reveal { opacity: 0; transform: translateY(24px); transition: opacity .9s ease, transform .9s ease; }
.eas-reveal.in { opacity: 1; transform: translateY(0); }

@keyframes easPulse { 0%,100% { opacity: 1; transform: scale(1);} 50% { opacity: 0.5; transform: scale(1.4);} }

/* Responsive grid utilities — desktop defaults, mobile stack */
.eas-grid-hero { display: grid; grid-template-columns: 1.4fr 1fr; gap: 80px; align-items: center; position: relative; z-index: 1; }
.eas-grid-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
.eas-grid-pillars { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; margin-top: 56px; background: rgba(255,255,255,0.08); border-radius: 22px; overflow: hidden; }
.eas-grid-numbers { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: rgba(255,255,255,0.08); border-radius: 22px; overflow: hidden; border: 1px solid rgba(255,255,255,0.08); margin-top: 48px; }
.eas-grid-manifesto { display: grid; grid-template-columns: 280px 1fr; gap: 80px; align-items: start; max-width: 1200px; margin: 0 auto; }
.eas-grid-footer { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; padding-bottom: 56px; border-bottom: 1px solid rgba(255,255,255,0.08); }
.eas-grid-system-module { display: grid; grid-template-columns: 1fr auto; gap: 24px; padding: 24px 0; border-bottom: 1px solid rgba(255,255,255,0.08); align-items: center; }
.eas-grid-guarantee { display: grid; grid-template-columns: 120px 1fr; gap: 32px; align-items: center; background: rgba(42,209,103,0.06); border: 1px solid rgba(42,209,103,0.2); border-radius: 22px; padding: 32px; }
.eas-grid-curriculum { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: rgba(255,255,255,0.08); border-radius: 22px; overflow: hidden; border: 1px solid rgba(255,255,255,0.08); margin-top: 56px; }

@media (max-width: 980px) {
    .eas-grid-hero { grid-template-columns: 1fr; gap: 48px; }
    .eas-grid-two-col { grid-template-columns: 1fr; gap: 40px; }
    .eas-grid-manifesto { grid-template-columns: 1fr; gap: 32px; max-width: 640px; }
    .eas-grid-numbers { grid-template-columns: repeat(2, 1fr); }
    .eas-grid-footer { grid-template-columns: 1fr 1fr; gap: 32px; }
    .eas-grid-curriculum { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 720px) {
    .eas-section, .eas-section--tight { padding: 80px 0; }
    .eas-container { padding: 0 20px; }
    .eas-grid-pillars { grid-template-columns: 1fr; margin-top: 40px; }
    .eas-grid-numbers { grid-template-columns: 1fr; }
    .eas-grid-footer { grid-template-columns: 1fr; gap: 24px; }
    .eas-grid-system-module { grid-template-columns: 1fr; gap: 12px; padding: 20px 0; }
    .eas-grid-guarantee { grid-template-columns: 1fr; gap: 18px; padding: 28px 22px; text-align: center; }
    .eas-grid-guarantee > :first-child { margin: 0 auto; width: 92px !important; height: 92px !important; }
    .eas-grid-guarantee > :first-child > :first-child { font-size: 28px !important; }
    .eas-grid-curriculum { grid-template-columns: 1fr; }
    /* Big inner padded blocks — shrink horizontal padding so content fits */
    .eas-pad-block { padding: 56px 28px !important; }
    .eas-pad-block-cta { padding: 72px 24px !important; }
    .eas-pad-stack { padding: 40px 22px !important; border-radius: 22px !important; }
    /* Manifesto sticky aside doesn't make sense when stacked */
    .eas-manifesto-side { position: static !important; }
    .eas-h1-hero { font-size: clamp(36px, 9vw, 56px) !important; }
    .eas-h1-sales { font-size: clamp(36px, 10vw, 60px) !important; }
    /* Sales-page sticky nav: shrink padding, hide price tagline so logo + Enroll fit */
    .eas-system-nav { padding: 12px 18px !important; gap: 8px !important; }
    .eas-system-nav-price { display: none !important; }
    /* Landing-page nav: hide section anchors so logo + Get EAS CTA fit */
    .eas-landing-nav { padding: 12px 18px !important; gap: 8px !important; }
    .eas-landing-nav-link { display: none !important; }
}
@media (max-width: 380px) {
    .eas-system-nav a span:nth-child(2) { font-size: 13px !important; }
}
`;
